import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonInput, IonSpinner,
} from "@ionic/react";
import { cameraOutline, scanOutline, closeOutline, checkmarkOutline } from "ionicons/icons";
import { useSavedAssets } from "@/components/SavedAssets";
import { extractStubText, parseStubText } from "@/utils/stubImport";
import { toast } from "@/utils/toast";

// "Import from an existing stub": snap a photo or upload the PDF of a pay
// stub from anywhere else and the details it carries prefill the form.
// Everything is read locally in the browser; the file goes nowhere.
// Pro/Enterprise — anyone else is walked to Billing.

const LABELS = {
  name: "Employee name", ssn: "SSN last 4", ssnLast4: "SSN last 4",
  employeeId: "Employee ID",
  address: "Street address", city: "City", state: "State", zip: "ZIP",
  company: "Company", companyAddress: "Company address", companyCity: "Company city",
  companyState: "Company state", companyZip: "Company ZIP",
  payType: "Pay type", rate: "Hourly rate", annualSalary: "Annual salary",
  payFrequency: "Pay frequency", hoursList: "Hours per period",
  startDateList: "Period start", endDateList: "Period end", payDateList: "Pay date",
  hireDate: "Hire date",
};
const ORDER = Object.keys(LABELS);

export default function StubImport({ mode = "paystub", onApply, compact = false, iconOnly = false }) {
  const { hasAccess, loading } = useSavedAssets();
  const fileRef = useRef(null);
  const [busyMsg, setBusyMsg] = useState("");
  const [found, setFound] = useState(null); // parsed fields awaiting review

  const pick = () => {
    if (loading) return;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setBusyMsg("Reading…");
      const text = await extractStubText(file, setBusyMsg);
      const { fields, employeeFields } = parseStubText(text);
      const use = mode === "employee" ? employeeFields : fields;
      if (!Object.keys(use).length) {
        toast.error("Nothing usable could be read from that file. A clearer photo or the original PDF works best.");
        return;
      }
      setFound(use);
    } catch (err) {
      console.error("Stub import failed:", err);
      toast.error("That file could not be read");
    } finally {
      setBusyMsg("");
    }
  };

  const apply = () => {
    const out = Object.fromEntries(Object.entries(found).filter(([, v]) => String(v).trim() !== ""));
    onApply?.(out);
    setFound(null);
    toast.success(`${Object.keys(out).length} field${Object.keys(out).length === 1 ? "" : "s"} filled in`);
  };

  const keys = found ? ORDER.filter((k) => found[k] !== undefined) : [];

  // Pro/Enterprise only: guests and Express never see the trigger at all.
  if (loading || !hasAccess) return null;

  return (
    <>
      {iconOnly ? (
        <IonButton
          fill="clear"
          size="small"
          className="stub-import-btn stub-import-icon"
          style={{ margin: 0, "--color": "var(--ion-text-color, #0d1b2a)" }}
          disabled={!!busyMsg}
          onClick={pick}
          aria-label="Import from an existing stub"
          title="Import from an existing stub: photograph or upload a stub from anywhere and its details fill this form"
        >
          {busyMsg
            ? <IonSpinner name="crescent" style={{ width: 18, height: 18 }} />
            : <IonIcon slot="icon-only" icon={scanOutline} style={{ fontSize: 20 }} />}
        </IonButton>
      ) : (
        <IonButton
          expand={compact ? undefined : "block"}
          size={compact ? "small" : undefined}
          fill="outline"
          className="stub-import-btn"
          style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}
          disabled={!!busyMsg}
          onClick={pick}
        >
          {busyMsg
            ? <><IonSpinner name="crescent" style={{ width: 16, height: 16, marginRight: 8 }} /> {busyMsg}</>
            : <><IonIcon slot="start" icon={cameraOutline} /> Import from an existing stub</>}
        </IonButton>
      )}
      <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={onFile} />

      {found && createPortal(
        <div className="stub-import-review-backdrop" style={{ position: "fixed", inset: 0, zIndex: 10060, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
          <div className="stub-import-review" style={{ width: "100%", maxWidth: 480, maxHeight: "88vh", display: "flex", flexDirection: "column", background: "var(--ion-background-color, #f4f5f8)", borderRadius: 8, overflow: "hidden" }}>
            <IonHeader>
              <IonToolbar>
                <IonButtons slot="start">
                  <IonButton onClick={() => setFound(null)} aria-label="Discard">
                    <IonIcon slot="icon-only" icon={closeOutline} className="bold-icon" style={{ fontSize: 26 }} />
                  </IonButton>
                </IonButtons>
                <IonTitle>Review import</IonTitle>
                <IonButtons slot="end">
                  <IonButton className="edit-sheet-save" onClick={apply} aria-label="Apply">
                    <IonIcon slot="icon-only" icon={checkmarkOutline} className="bold-icon" style={{ fontSize: 26 }} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <div style={{ overflowY: "auto", padding: "10px 16px 16px" }}>
              <p style={{ margin: "6px 0 12px", fontSize: "0.82rem", color: "var(--ion-color-medium)" }}>
                Read from the stub, nothing uploaded. Fix anything that looks off, then apply.
              </p>
              {keys.map((k) => (
                <IonInput
                  key={k}
                  className="stub-import-field"
                  fill="outline"
                  labelPlacement="floating"
                  label={LABELS[k]}
                  value={found[k]}
                  onIonInput={(e) => setFound((p) => ({ ...p, [k]: e.detail.value ?? "" }))}
                  style={{ marginBottom: 10 }}
                />
              ))}
            </div>
          </div>
        </div>,
        document.querySelector("ion-app") || document.body,
      )}
    </>
  );
}
